<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, inject, onMounted, provide, ref, watch } from 'vue';
import { VueCookies } from 'vue-cookies';
import { RouterView } from 'vue-router';

import GTMScript from '@/components/GTMScript.vue';
import QuebecLaw96 from '@/components/QuebecLaw96.vue';
import ConsentEmailDialog from '@/components/dialogs/ConsentEmailDialog.vue';
import CookieConsent from '@/components/dialogs/CookieConsent.vue';
import Footer from '@/components/layout/Footer.vue';
import Header from '@/components/layout/Header.vue';
import { setLocale } from '@/plugins/i18n';
import { useCommunityStore } from '@/store/communityStore';
import { useUserStore } from '@/store/userStore';
import { getTranslation } from '@/utils/data-display-utils';
import { setPrimaryColors } from '@/utils/style';
import PageLoading from '@/views/PageLoading.vue';
import { constant } from '@/constant';
import { getLabel } from '@/utils/data-display-utils';

// STORE
const communityStore = useCommunityStore();
const userStore = useUserStore();

// CONST
const cookies = inject<VueCookies>('$cookies');
const { isUserLogged } = storeToRefs(userStore);
const { community, isLoaded } = storeToRefs(communityStore);
const cookieConsentDialogRef = ref(null);
const consentDialogRef = ref(null);
const results = ref(false);

// LIFECYCLE
provide('results', results);

onMounted(async () => {
  setLocale(cookies.get('locale')?.toLowerCase() ?? community.value?.defaultLanguage ?? constant.DefaultLocale);
  setPrimaryColors();
});

// COMPUTED
const consentCookieValid = computed(() => {
  const consentCookie = cookies.get('consent');
  return consentCookie !== undefined && consentCookie !== null;
});
const consentCookieEnabled = computed(() => {
  const consentCookie = cookies.get('consent');
  if (consentCookie === undefined || consentCookie === null) return false;
  if (typeof consentCookie === 'string') {
    return consentCookie !== '0';
  }
  return Boolean(consentCookie);
});
const shouldShowTermsDialog = computed(() => userStore.isTermsApprovalRequired);

// FUNCTIONS
function updateResults(value: any) {
  results.value = value;
}

// WATCH
watch(isLoaded, () => {
  if (communityStore.community.languageEnabled.length === 1) {
    setLocale(communityStore.community.languageEnabled[0]);
  }
  if (!consentCookieValid.value) {
    cookieConsentDialogRef.value.openDialog();
  }
});
watch(isUserLogged, () => {
  if (shouldShowTermsDialog.value) {
    consentDialogRef.value.openDialog();
  }
});
</script>

<template>
  <div class="base-layout">
    <a class="base-layout__skip-link" href="#main-content">{{
      getLabel('aria-label.header.skip-to-content', community?.languageEnabled)
    }}</a>
    <div class="base-layout__container" v-if="isLoaded">
      <Header @results="updateResults($event)" :language-enabled="community?.languageEnabled" />
      <quebec-law96 :community="community" :language-enabled="community?.languageEnabled" />
      <main id="main-content" class="base-layout__page-content">
        <RouterView />
      </main>
      <Footer />
    </div>
    <div class="base-layout__container" v-else>
      <page-loading :language-enabled="community?.languageEnabled" />
    </div>
  </div>
  <consent-email-dialog
    ref="consentDialogRef"
    :community-title="isLoaded && getTranslation(community, 'title', community?.languageEnabled)"
    :organization-name="isLoaded && getTranslation(community, 'organizationName', community?.languageEnabled)"
    :language-enabled="community?.languageEnabled"
  />
  <cookie-consent ref="cookieConsentDialogRef" :language-enabled="community?.languageEnabled" />
  <GTMScript
    :gtm-id="communityStore.getCommunityGtmId"
    :gtm-enabled="communityStore.isCommunityGtmEnabled"
    :has-consent="consentCookieEnabled"
  />
</template>
<style lang="scss">
.base-layout {
  display: flex;
  min-height: 100vh;
  width: 100%;
  position: relative;

  &__skip-link {
    position: absolute;
    top: -100%;
    left: 0;
    z-index: 9999;
    padding: 8px 16px;
    background-color: $color-neutral-white;
    color: $color-neutral-black;
    font-weight: bold;
    text-decoration: underline;
    border: 2px solid $color-primary;
    border-radius: 0 0 4px 4px;
    &:focus {
      top: 0;
    }
  }

  &__container {
    display: flex;
    flex: auto;
    flex-direction: column;
    width: 100%;
  }
  &__page-content {
    height: 100%;
  }
}
.q-notification {
  &__actions i {
    color: #fff;
  }
  &__message a,
  &__message a:hover {
    border-bottom: none;
    color: #fff;
    text-decoration: underline;
  }
}
</style>
